import React, {useState} from 'react';
import classNames from 'classnames';

/* Validation */
import {Formik} from 'formik';
import validationSchema from './formConfig';

/* Gjensidige Core 20 Imports */
import utils from '@gjensidige/core-utilities-css';

/* Gjensidige Core 30 Imports */
import {Alert} from '@gjensidige/core-components/lib/alerts/default';
import {Col, Container, Row} from '@gjensidige/nci-core-grid';
import {Input} from '@gjensidige/core-components/lib/forms/input';
import {InputGroupSimple} from '@gjensidige/core-components/lib/forms/input-group-simple';

/* API */
import {QUEUEID} from './queue-id-constants';
import {STATUS_MESSAGE} from './statusMessages';
import {CallMeResult, getHttpStatusAndJson, sendCallMe} from "./callMe/sendCallMe";
import {Button} from "@gjensidige/core-components/lib/button";

const CallMe = () => {
    const [formData, setFormData] = useState({
        name: '',
        phoneNumber: '',
        isCustomer: '',
        subject: '',
        injurySubject: ''
    });
    const [queueId, setQueueId] = useState<QUEUEID>();
    const [statusMessage, setStatusMessage] = useState<string>('');
    const [statusCode, setStatusCode] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleFormData = (formData: any) => {
        setIsLoading(true);
        setStatusCode('');
        queueId && sendCallMe({
            phoneNumber: formData.phoneNumber,
            customerName: formData.name
        }, queueId).then((result) => getHttpStatusAndJson(result).then((parsed) => setResults(parsed)))
            .catch(() => {
                setStatusCode('-1')
                setStatusMessage(STATUS_MESSAGE.ERROR);
            })
    }

    const setResults = (callMeResult: CallMeResult) => {
        setStatusCode(callMeResult.data.code);
        setIsLoading(false)
        switch (callMeResult.data.code) {
            case '0':
                setStatusMessage(STATUS_MESSAGE.OK);
                break;
            case '3001':
                setStatusMessage(STATUS_MESSAGE.INVALID_PHONE);
                break;
            case '2010':
                setStatusMessage(STATUS_MESSAGE.ALREADY_REGISTERED);
                break;
            case '4005':
                setStatusMessage(STATUS_MESSAGE.CLOSED);
                break;
            case '-1':
            case '1200':
            case '15':
                setStatusMessage(STATUS_MESSAGE.ERROR);
                break;
        }
    }

    return (
        <Container className={utils['mt-2']}>
            {statusCode === '0' ? (
                <Row className={utils['justify-content-center']}>
                    <Alert
                        alertType="success"
                        showIcon
                        style={{whiteSpace: 'pre-line'}}
                        title="Tak for din henvendelse"
                        text={`Vi kontakter dig hurtigst muligt i tidsrummet man-tor: ${
                            formData.subject === QUEUEID.PrivatForsikring
                                ? '10:00-18:00 og fre 10:00-15:30.'
                                : '8:30-18:00 og fre 8:30-16:00.'
                        }`}
                        className={utils['mb-3']}
                    />
                </Row>
            ) : (
                <Formik
                    enableReinitialize
                    initialValues={{
                        name: formData.name,
                        phoneNumber: formData.phoneNumber,
                        isCustomer: formData.isCustomer,
                        subject: formData.subject,
                        injurySubject: formData.injurySubject
                    }}
                    validationSchema={validationSchema(formData)}
                    onSubmit={() => {
                        handleFormData(formData);
                    }}
                >
                    {(props) => {
                        const {
                            handleSubmit,
                            handleBlur,
                            setFieldValue,
                            touched,
                            errors
                        } = props;
                        return (
                            <form onSubmit={handleSubmit} className={utils['pb-3']}>
                                <Row
                                    className={classNames(
                                        utils['mb-3'],
                                        utils['justify-content-center']
                                    )}
                                    style={{justifyContent: 'center'}}
                                ></Row>
                                <Row className={utils['justify-content-center']}>
                                    <Col lg={6}>
                                        <Input
                                            id="name"
                                            labelText="Navn *"
                                            name="name"
                                            className={utils['pb-2']}
                                            onChange={(
                                                event: React.ChangeEvent<HTMLInputElement>
                                            ) => {
                                                setFieldValue('name', event.target.value);
                                                setFormData({...formData, name: event.target.value});
                                            }}
                                            onBlur={handleBlur}
                                            invalid={!!(touched.name && errors.name)}
                                            errorText={errors.name}
                                            value={formData.name}
                                        />
                                        <Input
                                            id="phone-number"
                                            labelText="Telefonnummer *"
                                            name="phoneNumber"
                                            className={utils['pb-2']}
                                            onChange={(
                                                event: React.ChangeEvent<HTMLInputElement>
                                            ) => {
                                                setFieldValue('phoneNumber', event.target.value);
                                                setFormData({
                                                    ...formData,
                                                    phoneNumber: event.target.value
                                                });
                                            }}
                                            onBlur={handleBlur}
                                            invalid={!!(touched.phoneNumber && errors.phoneNumber)}
                                            errorText={errors.phoneNumber}
                                            value={formData.phoneNumber}
                                        />
                                        <InputGroupSimple
                                            vertical
                                            header="Er du kunde hos os i dag? *"
                                            id="is-customer"
                                            className={utils['pb-2']}
                                            name="isCustomer"
                                            centered={false}
                                            onChange={(event) => {
                                                setFormData({
                                                    ...formData,
                                                    isCustomer: event.target.value,
                                                    subject: ''
                                                });
                                                setQueueId(undefined);
                                                setFieldValue('isCustomer', event.target.value);
                                            }}
                                            options={[
                                                {
                                                    label: 'Ja',
                                                    value: 'Ja'
                                                },
                                                {
                                                    label: 'Nej',
                                                    value: 'Nej'
                                                }
                                            ]}
                                            type="radio"
                                            value={formData.isCustomer}
                                            onBlur={handleBlur}
                                            invalid={!!(touched.isCustomer && errors.isCustomer)}
                                            errorText={errors.isCustomer}
                                        />
                                        {formData.isCustomer === 'Ja' ? (
                                            <InputGroupSimple
                                                vertical
                                                header="Hvad drejer din henvendelse sig om? *"
                                                id="radio-subject"
                                                className={utils['pb-2']}
                                                name="subject"
                                                centered={false}
                                                onChange={(event) => {
                                                    const value = event.target.value;
                                                    if (value !== 'injury') {
                                                        setQueueId(value as QUEUEID);
                                                    }
                                                    setFormData({
                                                        ...formData,
                                                        subject: event.target.value
                                                    });
                                                    setFieldValue('subject', event.target.value);
                                                }}
                                                options={[
                                                    {
                                                        label: 'Skade, tyveri eller uheld',
                                                        value: 'injury'
                                                    },
                                                    {
                                                        label: 'Sundhedsforsikring',
                                                        value: QUEUEID.SundhedsForsikring
                                                    },
                                                    {
                                                        label: 'Privatforsikring',
                                                        value: QUEUEID.PrivatForsikring
                                                    }
                                                ]}
                                                type="radio"
                                                value={formData.subject}
                                                onBlur={handleBlur}
                                                invalid={!!(touched.subject && errors.subject)}
                                                errorText={errors.subject}
                                            />
                                        ) : (
                                            formData.isCustomer === 'Nej' && (
                                                <InputGroupSimple
                                                    vertical
                                                    header="Hvad kan vi hjælpe dig med? *"
                                                    id="radio-subject-non-customer"
                                                    className={utils['pb-2']}
                                                    name="subject"
                                                    centered={false}
                                                    onChange={(event) => {
                                                        const value = event.target.value;
                                                        if (value !== 'injury') {
                                                            setQueueId(value as QUEUEID);
                                                        }
                                                        setFormData({
                                                            ...formData,
                                                            subject: event.target.value
                                                        });
                                                        setFieldValue('subject', event.target.value);
                                                    }}
                                                    options={[
                                                        {
                                                            label: 'Tilbud på en Sundhedforsikring',
                                                            value: QUEUEID.SundhedsForsikring
                                                        },
                                                        {
                                                            label: 'Tilbud på alle andre forsikringer',
                                                            value: QUEUEID.Salg
                                                        },
                                                        {
                                                            label: 'Henvendelse på vegne af en anden kunde',
                                                            value: QUEUEID.AndenKunde
                                                        },
                                                        {
                                                            label: 'Spørgsmål til en skadesag',
                                                            value: 'injury'
                                                        }
                                                    ]}
                                                    type="radio"
                                                    value={formData.subject}
                                                    onBlur={handleBlur}
                                                    invalid={!!(touched.subject && errors.subject)}
                                                    errorText={errors.subject}
                                                />
                                            )
                                        )}
                                        {formData.subject === 'injury' && (
                                            <InputGroupSimple
                                                vertical
                                                header="Hvilken forsikringen drejer henvendelsen sig om? *"
                                                id="radio-injurySubject"
                                                className={utils['pb-2']}
                                                name="injurySubject"
                                                centered={false}
                                                onChange={(event) => {
                                                    setQueueId(event.target.value as QUEUEID);
                                                    setFieldValue('injurySubject', event.target.value);
                                                }}
                                                options={[
                                                    {
                                                        label: 'Bil, MC, campingvogn',
                                                        value: QUEUEID.Bil
                                                    },
                                                    {
                                                        label: 'Indbo, hus, fritidshus og kæledyr',
                                                        value: QUEUEID.IndboHusKaeledyr
                                                    },
                                                    {
                                                        label: 'Ejerskifte',
                                                        value: QUEUEID.Ejerskifte
                                                    },
                                                    {
                                                        label: 'Rejse',
                                                        value: QUEUEID.Rejse
                                                    },
                                                    {
                                                        label: 'Ulykke',
                                                        value: QUEUEID.Ulykke
                                                    }
                                                ]}
                                                type="radio"
                                                value={queueId || ''}
                                                onBlur={handleBlur}
                                                invalid={
                                                    !!(touched.injurySubject && errors.injurySubject)
                                                }
                                                errorText={errors.injurySubject}
                                            />
                                        )}
                                        <Button variant={'critical'} loading={isLoading} type="submit" flexible>
                                            Ring mig op
                                        </Button>
                                    </Col>
                                </Row>
                            </form>
                        );
                    }}
                </Formik>
            )}
            {statusCode !== '0' && statusCode !== '' && (
                <Row className={utils['justify-content-center']}>
                    <Col lg={6}>
                        <Alert
                            alertType="error"
                            showIcon
                            title="Der skete en fejl."
                            text={statusMessage}
                            className={classNames(utils['mb-3'])}
                        />
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default CallMe;
